'use client';

import { c as _c } from "react/compiler-runtime";
import type { CardAIMode } from '@shared/all/domains/electron';
import { createContext, type ReactNode, useContext, useState } from 'react';
import JobTerminalOverlay from '../components/common/JobTerminalOverlay';
import SessionLaunchOverlay from '../components/common/SessionLaunchOverlay';
import { showTerminalWindow } from '../helpers/terminalWindow';
import { useDetachedTerminal } from './useDetachedTerminal';

/** A live runner job, as much as the Running jobs panel knows about it. */
export type JobTarget = {
  spaceSlug: string;
  postId: string;
  title: string;
  mode: CardAIMode;
  runnerId: number;
};

/** A board to start a new session on. `runnerId` when the caller knows which
 *  machine the board is on (it has a job running there). */
export type LaunchTarget = {
  spaceSlug: string;
  runnerId?: number;
};
type JobTerminalContextValue = {
  /** Card the overlay currently shows, or null when it is closed. */
  openJobKey: string | null;
  openJob: (job: JobTarget) => void;
  closeJob: () => void;
  /** Start a new session on a board without navigating to it. */
  launchSession: (target: LaunchTarget) => void;
};
const JobTerminalContext = createContext<JobTerminalContextValue>({
  openJobKey: null,
  openJob: () => {},
  closeJob: () => {},
  launchSession: () => {}
});
const jobKey = (job: JobTarget) => `${job.spaceSlug}::${job.postId}`;

// One overlay at a time: opening a job replaces a pending launch and the other
// way round, so the two can never stack two terminal windows.
type Shown = {
  kind: 'job';
  job: JobTarget;
} | {
  kind: 'launch';
  target: LaunchTarget;
  nonce: string;
};

/**
 * Hosts the card terminal above the whole app, so clicking a job in the
 * Running jobs panel opens its session in place. The page underneath keeps its
 * state, and closing the overlay returns you to the panel to open the next
 * job — a board deep link would have thrown that page away.
 */
export function JobTerminalProvider(t0) {
  const $ = _c(20);
  const {
    children
  } = t0;
  const [shown, setShown] = useState(null);
  const detached = useDetachedTerminal();
  let t1;
  if ($[0] !== detached) {
    t1 = next => {
      if (next.mode === "bare" && detached) {
        showTerminalWindow({
          spaceSlug: next.spaceSlug,
          postId: next.postId,
          title: next.title,
          runnerId: next.runnerId
        });
        return;
      }
      setShown({
        kind: "job",
        job: next
      });
    };
    $[0] = detached;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const openJob = t1;
  let t2;
  if ($[2] !== detached) {
    t2 = target => {
      const nonce = Date.now().toString(36);
      if (detached) {
        showTerminalWindow({
          ...target,
          launch: nonce
        });
        return;
      }
      setShown({
        kind: "launch",
        target,
        nonce
      });
    };
    $[2] = detached;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const launchSession = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setShown(null);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const close = t3;
  let t4;
  if ($[5] !== shown) {
    t4 = shown?.kind === "job" ? jobKey(shown.job) : null;
    $[5] = shown;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== launchSession || $[8] !== openJob || $[9] !== t4) {
    t5 = {
      openJobKey: t4,
      openJob,
      closeJob: close,
      launchSession
    };
    $[7] = launchSession;
    $[8] = openJob;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== shown) {
    t6 = shown?.kind === "job" && <JobTerminalOverlay key={jobKey(shown.job)} job={shown.job} onClose={close} />;
    $[11] = shown;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== shown) {
    t7 = shown?.kind === "launch" && <SessionLaunchOverlay key={`${shown.target.spaceSlug}::${shown.nonce}`} target={shown.target} onClose={close} />;
    $[13] = shown;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== children || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <JobTerminalContext.Provider value={t5}>{children}{t6}{t7}</JobTerminalContext.Provider>;
    $[15] = children;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
}
export function useJobTerminal() {
  return useContext(JobTerminalContext);
}